import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Delete as DeleteIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import { userService } from '../services/userService';
import { formatDateToTR } from '../utils/dateUtils';
import { tenantService } from '../services/tenantService';
import type { UserTenant, User, UserRole } from '../types/user';
import type { Tenant } from '../types/tenant';
import { PermissionType, UserRole as UserRoleEnum } from '../types/user';

const permissionLabels: Record<PermissionType, string> = {
  [PermissionType.Read]: 'Okuma',
  [PermissionType.Admin]: 'Yönetici',
};

const roleLabels: Record<UserRole, string> = {
  [UserRoleEnum.SystemAdmin]: 'Sistem Yöneticisi',
  [UserRoleEnum.TenantAdmin]: 'Şirket Grubu Yöneticisi',
  [UserRoleEnum.Viewer]: 'Görüntüleyici',
};

const UserPermissions = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [userTenants, setUserTenants] = useState<UserTenant[]>([]);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [selectedPermission, setSelectedPermission] = useState<PermissionType>(
    PermissionType.Read as PermissionType
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<UserTenant | null>(null);

  // Role değerini normalize et (backend'den string enum ismi olarak gelebilir)
  const normalizeRole = (role: UserRole | string | number): UserRole => {
    if (typeof role === 'number') {
      return role as UserRole;
    }
    if (typeof role === 'string') {
      const num = parseInt(role, 10);
      if (!isNaN(num) && (num === UserRoleEnum.SystemAdmin || num === UserRoleEnum.TenantAdmin || num === UserRoleEnum.Viewer)) {
        return num as UserRole;
      }
      // String enum isimlerini kontrol et
      const roleStr = role.trim();
      const roleLower = roleStr.toLowerCase();
      if (roleStr === 'SystemAdmin' || roleLower === 'systemadmin' || roleLower === 'system_admin') {
        return UserRoleEnum.SystemAdmin;
      } else if (roleStr === 'TenantAdmin' || roleLower === 'tenantadmin' || roleLower === 'tenant_admin') {
        return UserRoleEnum.TenantAdmin;
      } else if (roleStr === 'Viewer' || roleLower === 'viewer') {
        return UserRoleEnum.Viewer;
      }
    }
    return UserRoleEnum.Viewer; // Fallback
  };

  const loadData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const [users, tenants, permissions] = await Promise.all([
        userService.getAll(),
        tenantService.getAll(),
        userService.getUserTenants(id),
      ]);

      // Kullanıcı bilgisini bul ve role'ü normalize et
      const foundUser = users.find(u => u.id === id);
      if (foundUser) {
        setUser({
          ...foundUser,
          role: normalizeRole(foundUser.role),
        });
      }

      setAvailableTenants(tenants);
      setUserTenants(permissions);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id, loadData]);

  const handleAssignPermission = async () => {
    if (!id || !selectedTenantId) {
      alert('Lütfen şirket grubu seçiniz');
      return;
    }

    try {
      await userService.assignTenantPermission({
        userId: id,
        tenantId: selectedTenantId,
        permissionType: selectedPermission,
      });
      await loadData();
      setSelectedTenantId('');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Yetki atanırken bir hata oluştu');
    }
  };

  const handleRemoveClick = (userTenant: UserTenant) => {
    setSelectedTenant(userTenant);
    setDeleteDialogOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!id || !selectedTenant) return;

    try {
      await userService.removeTenantPermission(id, selectedTenant.tenantId);
      await loadData();
      setDeleteDialogOpen(false);
      setSelectedTenant(null);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Yetki kaldırılırken bir hata oluştu');
    }
  };

  const getAvailableTenantsForSelection = () => {
    const assignedTenantIds = userTenants.map((ut) => ut.tenantId);
    return availableTenants.filter((t) => !assignedTenantIds.includes(t.id));
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Kullanıcı Yetkileri
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/users')}
        >
          Geri Dön
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Kullanıcı Bilgileri */}
      {user && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Kullanıcı Bilgileri
          </Typography>
          <Box display="flex" gap={3} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Kullanıcı Adı
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {user.username}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                E-posta
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {user.email}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Rol
              </Typography>
              <Chip
                label={roleLabels[user.role]}
                size="small"
                color={
                  user.role === UserRoleEnum.SystemAdmin
                    ? 'error'
                    : user.role === UserRoleEnum.TenantAdmin
                    ? 'warning'
                    : 'default'
                }
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Yeni Yetki Ata
        </Typography>
        <Box display="flex" gap={2} mt={2}>
          <FormControl fullWidth>
            <InputLabel>Şirket Grubu</InputLabel>
            <Select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              label="Şirket Grubu"
            >
              <MenuItem value="">Şirket grubu seçiniz</MenuItem>
              {getAvailableTenantsForSelection().map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Yetki Tipi</InputLabel>
            <Select
              value={selectedPermission}
              onChange={(e) => setSelectedPermission(Number(e.target.value) as PermissionType)}
              label="Yetki Tipi"
            >
              <MenuItem value={PermissionType.Read as number}>Okuma</MenuItem>
              <MenuItem value={PermissionType.Admin as number}>Yönetici</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleAssignPermission}
            disabled={!selectedTenantId}
            sx={{ minWidth: 150 }}
          >
            Yetki Ata
          </Button>
        </Box>
      </Paper>

      <Paper>
        <Box p={2} borderBottom="1px solid" borderColor="divider">
          <Typography variant="h6">Mevcut Yetkiler</Typography>
        </Box>
        {userTenants.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">
              Henüz atanmış yetki bulunmuyor.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Şirket Grubu Adı</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Yetki Tipi</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Atanma Tarihi</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }} align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userTenants.map((userTenant, index) => (
                  <TableRow
                    key={userTenant.tenantId}
                    hover
                    sx={{
                      bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
                      '&:hover': { bgcolor: 'action.selected' },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{userTenant.tenantName}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Chip
                        label={permissionLabels[userTenant.permissionType]}
                        size="small"
                        sx={{ height: 22, fontSize: '0.75rem' }}
                        color={userTenant.permissionType === PermissionType.Admin ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      {formatDateToTR(userTenant.createdAt)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }} align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveClick(userTenant)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Yetki Kaldır</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedTenant?.tenantName} şirket grubu için yetkiyi kaldırmak istediğinize emin misiniz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleRemoveConfirm} color="error" variant="contained">
            Kaldır
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default UserPermissions;

